import { Deserializer } from "jsonapi-serializer";
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "../constants/response-status-codes";
import { genericError } from "../models/GenericError";
import { Holiday } from "../models/Holiday";
import { ListQuery } from "../models/ListQuery";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { HolidayWrapper } from "../models/wrappers/HolidayWrapper";
import { convertDateToNumber, convertNumberToDate } from "../utilities/DateUtils";
import { APIConnector } from "./api/APIConnector";
import { getFormattedDate } from './utilities/dateFormatting';
import { createPagination } from "./utilities/paginationBuilder";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";

const apiConnector = new APIConnector();

export const getHolidays = async (listParameters?: ListQuery): Promise<HolidayWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm)
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const fieldsHolidays = 'fields[holiday]=holName,holDateStart,holDateEnd,site';
        const fieldsSite = 'fields[site]=siteName';
        const url = `holiday?include=site${partialURL}&${fieldsHolidays}&${fieldsSite}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const rows: Holiday[] = data.map((holiday): Holiday => {
                    return {
                        id: holiday['id'],
                        name: holiday['hol-name'],
                        endDate: getFormattedDate(holiday['hol-date-end']) || '',
                        site: holiday['site']['site-name'],
                        startDate: getFormattedDate((holiday['hol-date-start'])) || ''
                    }
                })

                return resolve({ rows, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getHolidayById = async (holidayId: string): Promise<Holiday> => {
    try {
        const url = `holiday/${holidayId}?include=site`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {

                if (error) {
                    reject({ status: response.status, message: genericError });
                }

                if (data['id']) {
                    const holiday: Holiday = {
                        id: data['id'],
                        name: data['hol-name'],
                        endDate: convertNumberToDate(data['hol-date-end']),
                        startDate: convertNumberToDate((data['hol-date-start'])),
                        site: data['site']['site-name'],
                        siteId: data['site']['id'],
                        description: data['hol-desc'] ?? ''
                    }

                    return resolve(holiday);
                }

                return reject(processServerError(error));
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getHolidayNamesWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const url = `holiday?fields[holiday]=holName${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    reject({ status: response.status, message: genericError });
                }

                const holiday: Name[] = data.map(holiday => { return { label: holiday['hol-name'], value: holiday['id'] } });
                return resolve({ rows: holiday, pagination: newPagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeHoliday = async (holiday: Holiday, createHoliday = true): Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            data: {
                type: "holiday",
                id: holiday.id,
                attributes: {
                    holDateEnd: convertDateToNumber(holiday.endDate),
                    holDateStart: convertDateToNumber(holiday.startDate),
                    holName: holiday.name,
                    holDesc: holiday.description ? holiday.description : '',
                },
                relationships: {
                    site: {
                        data: {
                            type: "site",
                            id: holiday.siteId
                        }
                    }
                },
            }
        }

        const url = `holiday${createHoliday ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createHoliday, newRecord).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject({ status: response.status, message: genericError })
            })
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createHoliday) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }

                    new Deserializer().deserialize(content, (error, holiday) => {
                        if (error) {
                            return reject({ status: response.status, id: holiday.id, success: false, message: genericError });
                        }
                        return resolve({ id: holiday.id, success: true });
                    })

                    return;
                }
                if (response.status === NO_CONTENT) {
                    return resolve({ id: holiday.id, success: true })
                }

                const json = await response.json();
                return reject({ status: response.status, id: holiday.id, success: false, message: getConstraintViolation(json.errors[0].detail) });
            }

            return reject({ status: INTERNAL_SERVER_ERROR, id: holiday.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deleteHoliday = async (id: string): Promise<{ id: string, success: boolean }> => {
    try {
        const url = `holiday/${id}`;
        const holiday = {
            data: {
                type: "holiday",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, holiday)

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();
            return reject({ status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
